import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/shared/store/authStore';
import { logout } from '@/shared/services/authService';
import { useIncidentsListener } from '@/shared/hooks/useIncidentsListener';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import styles from './DashboardLayout.module.css';

export function DashboardLayout() {
  const { user, clearAuth } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { unreadCount, isMuted, toggleMute } = useIncidentsListener();

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
      clearAuth();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Forzar limpieza del estado local aunque falle Firebase
      clearAuth();
      navigate('/login');
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className={styles.container}>
      {/* Botón hamburguesa para móvil */}
      <button
        className={styles.menuButton}
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        <svg
          className={styles.menuIcon}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {/* Notification and Mute (Top Right) */}
      <div className={styles.topActions}>
        <button
          className={styles.muteButton}
          onClick={toggleMute}
          title={isMuted ? "Activar Sonido" : "Silenciar Notificaciones"}
        >
          {isMuted ? (
            <svg className={styles.muteIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
              <line x1="23" y1="9" x2="17" y2="15"></line>
              <line x1="17" y1="9" x2="23" y2="15"></line>
            </svg>
          ) : (
            <svg className={styles.muteIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
            </svg>
          )}
        </button>

        <div className={styles.notificationWrapper}>
          <button
            className={styles.notificationButton}
            onClick={() => navigate('/incidents')}
            title="Ver Incidencias"
          >
            <svg className={styles.bellIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            {unreadCount > 0 && (
              <span className={styles.notificationBadge}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Overlay para móvil */}
      {sidebarOpen && (
        <div className={styles.overlay} onClick={closeSidebar} />
      )}

      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.logo}>
          <div className={styles.logoHeader}>
            <h1>EncoreEats</h1>
            <button
              className={styles.closeButton}
              onClick={closeSidebar}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
          <p>Admin Dashboard</p>
        </div>
        <nav className={styles.nav}>
          <Link
            to="/dashboard"
            className={`${styles.navLink} ${isActive('/dashboard') ? styles.active : ''}`}
          >
            Dashboard
          </Link>
          <Link
            to="/restaurants/pending"
            className={`${styles.navLink} ${isActive('/restaurants/pending') ? styles.active : ''}`}
          >
            Solicitudes Pendientes
          </Link>
          <Link
            to="/restaurants/rejected"
            className={`${styles.navLink} ${isActive('/restaurants/rejected') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Solicitudes Rechazadas
          </Link>
          <Link
            to="/restaurants"
            className={`${styles.navLink} ${isActive('/restaurants') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Comercios
          </Link>
          <Link
            to="/users"
            className={`${styles.navLink} ${isActive('/users') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Usuarios
          </Link>
          <Link
            to="/banners"
            className={`${styles.navLink} ${isActive('/banners') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Banners Promocionales
          </Link>
          {/* <Link
            to="/categories"
            className={`${styles.navLink} ${isActive('/categories') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Categorías de Comercios
          </Link> */}
          <Link
            to="/settlements"
            className={`${styles.navLink} ${isActive('/settlements') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Liquidaciones
          </Link>
          <Link
            to="/cash-handovers"
            className={`${styles.navLink} ${isActive('/cash-handovers') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Entregas en Efectivo
          </Link>
          <Link
            to="/coupons"
            className={`${styles.navLink} ${isActive('/coupons') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Cupones (Supervisión)
          </Link>
          <Link
            to="/couriers"
            className={`${styles.navLink} ${isActive('/couriers') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Repartidores (Couriers)
          </Link>
          <Link
            to="/audit-logs"
            className={`${styles.navLink} ${isActive('/audit-logs') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Auditoría (Logs)
          </Link>
          <Link
            to="/orders"
            className={`${styles.navLink} ${isActive('/orders') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Pedidos (Monitor)
          </Link>
          <Link
            to="/incidents"
            className={`${styles.navLink} ${isActive('/incidents') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Incidencias
          </Link>
          <Link
            to="/config"
            className={`${styles.navLink} ${isActive('/config') ? styles.active : ''}`}
            onClick={closeSidebar}
          >
            Configuración
          </Link>
        </nav>
        <div className={styles.userSection}>
          <div className={styles.userInfo}>
            <p className={styles.userEmail}>{user?.email}</p>
            <p className={styles.userRole}>{user?.role}</p>
          </div>
          <button onClick={handleLogout} className={styles.logoutButton}>
            Cerrar Sesión
          </button>
        </div>
      </aside>
      <main className={styles.main}>
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div >
  );
}

